import GridHeader from '@/components/GridHeader';
import SongGrid from '@/components/SongGrid';
import { SessionContext } from '@/context/SessionContext';
import { Artist, Genre, Song } from '@/javascript/library-objects';
import { Instrument } from '@/javascript/music-objects';
import {
	ActionIcon, Box, rem, Select,
	Spoiler,
	Stack,
	Text,
	TextInput,
	useMantineTheme
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconArrowRight, IconSearch } from '@tabler/icons-react';
import { useContext, useEffect, useState } from 'react';

const SearchResultsPage: React.FC = () => {
	const theme = useMantineTheme();
	const [genres, setGenres] = useState<Genre[]>([]);
	const [instruments, setInstruments] = useState<Instrument[]>([]);
	const [artists, setArtists] = useState<Artist[]>([]);

	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

	const session = useContext(SessionContext);
	if (!session) {
		throw new Error('SearchResultsPage must be used within a SessionProvider.');
	}

	// State to store the full (filtered) list of songs
	const [songs, setSongs] = useState<Song[] | null>(null);

	// State for selected filter values and search input
	const [searchQuery, setSearchQuery] = useState('');
	const [selectedGenreId, setSelectedGenreId] = useState<string | null>(null);
	const [selectedInstrumentId, setSelectedInstrumentId] = useState<string | null>(null);
	const [selectedArtistId, setSelectedArtistId] = useState<string | null>(null);

	// Move filter open/close out of Spoiler for better control if needed (optional)
	// const [filtersOpened, setFiltersOpened] = useState(false);

	// Fetch songs and filter them whenever filters or search input change
	useEffect(() => {
		const allSongs = session.marketplaceSongs;
		if (allSongs === null) {
			setSongs(null);
			return;
		}

		const filtered = allSongs.filter((song: Song) => {
			const matchesGenre = !selectedGenreId || song.genres.some(g => g.genreId === selectedGenreId);
			const matchesInstrument = !selectedInstrumentId || song.instruments.some(i => i.id === selectedInstrumentId);
			const matchesArtist = !selectedArtistId || song.artists.some(a => a.artistId === selectedArtistId);
			const matchesSearch = !searchQuery || song.songName.toLowerCase().includes(searchQuery.toLowerCase());
			return matchesGenre && matchesInstrument && matchesArtist && matchesSearch;
		});
		setSongs(filtered);

		// Build filter data based on filtered songs
		setGenres(filtered.reduce((acc: Genre[], song: Song) => {
			song.genres.forEach(g => {
				if (!acc.some(existing => existing.genreId === g.genreId)) {
					acc.push(g);
				}
			});
			return acc;
		}, []));

		setInstruments(filtered.reduce((acc: Instrument[], song: Song) => {
			song.instruments.forEach(i => {
				if (!acc.some(existing => existing.id === i.id)) {
					acc.push(i);
				}
			});
			return acc;
		}, []));

		setArtists(filtered.reduce((acc: Artist[], song: Song) => {
			song.artists.forEach(a => {
				if (!acc.some(existing => existing.artistId === a.artistId)) {
					acc.push(a);
				}
			});
			return acc;
		}, []));
	}, [selectedGenreId, selectedInstrumentId, selectedArtistId, searchQuery, session.marketplaceSongs]);




	return (
		<Stack align={"center"} justify={"center"}>
			<GridHeader>
				<Text
					p={20}
					ml={isMobile ? 10 : 20}
					fz={isMobile ? 15 : 45}
					c={theme.colors.blue[0]}
					ta="center"
					style={{
						minWidth: 0,
					}}
					truncate
				>
					Search
				</Text>
				{/* Search row, with Spoiler next to search bar */}
				<Box style={{ flex: 1, display: "flex", alignItems: "center", gap: rem(16), justifyContent: "center" }}>
					<TextInput
						radius="xl"
						size={isMobile ? "xs" : "md"}
						placeholder="Search songs..."
						rightSectionWidth={42}
						leftSection={<IconSearch size={18} stroke={1.5} color={"#5A80A0"} />}
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.currentTarget.value)}
						rightSection={
							<ActionIcon size={isMobile ? 20 : 32} radius="xl" color={"#5A80A0"} variant="filled">
								<IconArrowRight size={isMobile ? 10 : 18} stroke={1.5} color={"#E8F1F9"} />
							</ActionIcon>
						}
						style={{ maxWidth: rem(260), width: "100%" }}
					/>
					<Spoiler
						maxHeight={0}
						showLabel={
							<Text size={isMobile ? "xs" : "sm"} c="white">
								Show filters
							</Text>
						}
						hideLabel={
							<Text size={isMobile ? "xs" : "sm"}  c="white">
								Hide filters
							</Text>
						}
					>
						<Select
							label="Genre"
							placeholder="Select genre"
							data={genres.map(g => ({ value: g.genreId, label: g.genreName }))}
							value={selectedGenreId}
							onChange={setSelectedGenreId}
							clearable
							styles={{ input: { color: "black" }, label: { color: "white" }, dropdown: { color: "black" } }}
						/>
						<Select
							label="Instrument"
							placeholder="Select instrument"
							data={instruments.map(i => ({ value: i.id, label: i.name }))}
							value={selectedInstrumentId}
							onChange={setSelectedInstrumentId}
							clearable
							styles={{ input: { color: "black" }, label: { color: "white" }, dropdown: { color: "black" } }}
						/>
						<Select
							label="Artist"
							placeholder="Select artist"
							data={artists.map(a => ({ value: a.artistId, label: a.artistName }))}
							value={selectedArtistId}
							onChange={setSelectedArtistId}
							clearable
							styles={{ input: { color: "black" }, label: { color: "white" }, dropdown: { color: "black" } }}
						/>
					</Spoiler>
				</Box>
				<Stack
					style={{
						minWidth: 0,
						alignItems: "flex-end",
						justifyContent: "flex-end",
					}}
					gap={0}
				>
					{/* You could move other header content here if needed */}
				</Stack>
			</GridHeader>
			<Box w={"100%"} mt={200}>
				{
					<SongGrid
						songs={songs}
						type="marketplace"
						skeletonSize={16}
					/>
				}
			</Box>
		</Stack >
	);
};

export default SearchResultsPage;